import React, { ReactNode, useEffect } from 'react';
import { Col, Form, Modal, Row, message } from 'antd';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Title } from '@/components/Typography';
import { I18n } from '@aws-amplify/core';

import { Generate2FAForm, MethodsContainer, Subtitle } from './styles';
import { ConfirmPasswordModal } from '../ConfirmPasswordModal';
import { GenerateAuthApp2FA } from './GenerateAuthApp2FA';
import { GenerateEmail2FA } from './GenerateEmail2FA';
import { RecoveryTokens } from './RecoveryTokens';
import { useTFAContext } from '../context';
import { Validate } from '../Validate';
import { Methods } from './Methods';
import { Buttons } from './Buttons';

const { useForm } = Form;

interface StepsProps {
	content: ReactNode;
}

export const Generate2FAModal = () => {
	const [form] = useForm();
	const {
		openGenerateTokenModal,
		currentStep,
		user2FA,
		generated2FA,
		selected2FAType,
		disabled,
		correctPassword,
		recoveryTokens
	} = useTFAContext();
	const { handle2FAChecked, handleDelete2FA, handleResetStep, handleOpenGenerateTokenModal, handleCorrectPassword } =
		useTFAContext();

	useEffect(() => {
		handleCorrectPassword(false);
	}, []);

	function handleClose() {
		if (disabled) {
			return message.error(I18n.get("Can't close yet. Read the instructions."));
		}
		if (generated2FA.id && currentStep === 2) {
			Modal.confirm({
				title: I18n.get('Heads up! Closing now will delete the token you generated. Want to continue?'),
				icon: <ExclamationCircleOutlined />,
				okText: I18n.get('Confirm'),
				cancelText: I18n.get('Cancel'),
				okType: 'danger',
				onOk: async () => {
					handleConfirmClose();
					await handleDelete2FA();
				}
			});
		} else {
			handleConfirmClose();
		}
	}

	function handleConfirmClose() {
		form.resetFields();
		handle2FAChecked();
		handleCorrectPassword(false);
		handleOpenGenerateTokenModal(false);

		if (!user2FA.id || !!user2FA.validated) {
			handleResetStep();
		}
	}

	const steps: StepsProps[] = [
		{
			content: <Methods />
		},
		{
			content: selected2FAType === 'EMAIL' ? <GenerateEmail2FA /> : <GenerateAuthApp2FA />
		},
		{
			content: <Validate />
		},
		{
			content: <RecoveryTokens />
		}
	];

	return correctPassword || recoveryTokens.length > 0 ? (
		<Modal open={openGenerateTokenModal} onCancel={handleClose} width={600} footer={false} centered>
			<Generate2FAForm form={form} layout="vertical" style={{ minHeight: '250px' }}>
				<Row align="middle">
					<Col span={24}>
						<Row justify="center" gutter={[16, 16]}>
							<Col span='auto'>
								<Title level={3}>Generate Two-factor authentication</Title>
							</Col>
							{currentStep === 0 && (
								<Col span={24}>
									<Subtitle justify='center'>
										{I18n.get('Select a method to generate your two-factor authentication')}
									</Subtitle>
								</Col>
							)}
						</Row>
						<MethodsContainer justify="center" align="middle">
							{steps[currentStep].content}
						</MethodsContainer>
					</Col>
				</Row>
				<Row>
					<Buttons resetFields={form.resetFields} totalSteps={steps.length - 1} />
				</Row>
			</Generate2FAForm>
		</Modal>
	) : (
		<ConfirmPasswordModal />
	);
};
